/**
 * Portal Manta · TipoRodoviaPageV2
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { V, S, fmtNum, sumBy, sigKey, fmtBRL, isAmpliacao, OBRAS_ROD } from '../../globals.js';
import { M } from '../../tokens/colors.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

function TipoRodoviaPageV2({lote}){
  const sig = sigKey(lote);
  const cf = CONSOL_FIN[sig]||{};
  const od = OBRAS_ROD[sig]||{};
  const sistema = od.sistema||[];
  const extLote = sistema.reduce((s,r)=>s+(r.ext||0),0)||1;
  const tips = (lote.tipologias||[]).filter(isAmpliacao);
  const totSicro = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const cores = {'PAVIMENTACAO':V.or,'TERRAPLENOS E CONTENCOES':V.bl,'DRENAGEM':V.gn,
                 'OAES':V.rxn||'#7c3aed','SINALIZACAO':V.am||'#d97706',
                 'ILUMINACAO':V.ind||'#0891b2','SERVICOS PRELIMINARES':V.gr||'#475569'};
  const tipoCores = {duplicacoes:'#1d4ed8',contornos:'#7c3aed',faixas:'#ea580c',marginais:'#16a34a',
                     ciclovias:'#db2777',oaes:'#7c3aed',passarelas:'#0891b2',
                     diamantes:'#f59e0b',trombetas:'#84cc16',parclos:'#10b981',rotatorias:'#94a3b8'};
  // Tipos de obra (categorias OBRAS_ROD)
  const tipos = [
    {k:'duplicacoes',l:'Duplicação',field:'km',unid:'km'},
    {k:'contornos',l:'Contornos',field:'km',unid:'km'},
    {k:'faixas',l:'Faixa Adic.',field:'km',unid:'km'},
    {k:'marginais',l:'Marginais',field:'km',unid:'km'},
    {k:'ciclovias',l:'Ciclovias',field:'km',unid:'km'},
    {k:'oaes',l:'OAEs',field:'n',unid:'un'},
    {k:'passarelas',l:'Passarelas',field:'n',unid:'un'},
    {k:'diamantes',l:'Diamantes',field:'n',unid:'un'},
    {k:'trombetas',l:'Trombetas',field:'n',unid:'un'},
    {k:'parclos',l:'Parclos',field:'n',unid:'un'},
    {k:'rotatorias',l:'Rotatórias',field:'n',unid:'un'},
  ];
  const sumTipo = (t,rod=null)=>{
    const items = (od[t.k]||[]).filter(x=>!rod||x.rod===rod);
    return items.reduce((s,x)=>s+(x[t.field]||(t.field==='n'?1:0)),0);
  };
  // Por disciplina: total + quantidades
  const disciplinas = useMemo(()=>{
    return Object.entries(cf).map(([d,items])=>{
      const tot = sumBy(items,'v');
      const totQty = sumBy(items,'q');
      // agrupar quantidades por unidade
      const porUnid = {};
      items.forEach(it=>{
        const u = (it.u||'?').trim();
        porUnid[u] = (porUnid[u]||0)+(it.q||0);
      });
      return {disc:d,tot,totQty,porUnid};
    }).sort((a,b)=>b.tot-a.tot);
  },[sig]);
  return(<div style={{padding:'12px 16px'}}>

    {/* BLOCO 1 · TOTAL GERAL */}
    <div style={{fontSize:11,fontWeight:700,color:V.br,letterSpacing:.4,marginBottom:8,paddingBottom:4,borderBottom:'2px solid '+V.bl}}>
      ▣ BLOCO 1 · TOTAL GERAL DAS OBRAS DE AMPLIAÇÃO
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10,marginBottom:18}}>
      <div style={{...S.cardP,borderLeft:'4px solid '+V.bl}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>VALOR TOTAL SICRO (lote)</div>
        <div style={{fontSize:18,fontWeight:700,color:V.br,marginTop:4}}>{fmtBRL(totSicro)}</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>{Object.values(cf).reduce((s,a)=>s+a.length,0)} itens · 7 disciplinas</div>
      </div>
      <div style={{...S.cardP,borderLeft:'4px solid '+V.gn}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>EXTENSÃO DO LOTE</div>
        <div style={{fontSize:18,fontWeight:700,color:V.br,marginTop:4}}>{fmtNum(extLote,1)} km</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>{sistema.length} rodovias</div>
      </div>
      <div style={{...S.cardP,borderLeft:'4px solid '+V.or}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>R$ MÉDIO POR KM</div>
        <div style={{fontSize:18,fontWeight:700,color:V.br,marginTop:4}}>{fmtBRL(extLote?totSicro/extLote:0,0)}/km</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>todas disciplinas</div>
      </div>
      <div style={{...S.cardP,borderLeft:'4px solid '+(V.am||'#d97706')}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>TIPOS DE AMPLIAÇÃO</div>
        <div style={{fontSize:18,fontWeight:700,color:V.br,marginTop:4}}>{tips.length}</div>
        <div style={{fontSize:10,color:V.gr,marginTop:2}}>códigos PER (A1-A11)</div>
      </div>
    </div>

    {/* BLOCO 2 · TOTAL × SERVIÇOS (disciplinas) */}
    <div style={{fontSize:11,fontWeight:700,color:V.br,letterSpacing:.4,marginBottom:8,paddingBottom:4,borderBottom:'2px solid '+V.bl}}>
      ▣ BLOCO 2 · TOTAL LIGADO AOS SERVIÇOS (disciplinas SICRO)
    </div>
    <div style={{...S.card,overflow:'auto',marginBottom:18}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={THl}>Serviço (disciplina)</th>
          <th style={TH}>R$ total</th>
          <th style={TH}>% lote</th>
          <th style={TH}>R$/km</th>
          <th style={THl}>Quantidades por unidade · TOTAL · /km</th>
        </tr></thead>
        <tbody>
          {disciplinas.map(d=>{
            const pct = totSicro?d.tot/totSicro*100:0;
            const rkm = extLote?d.tot/extLote:0;
            const cor = cores[d.disc]||V.bl;
            // ordena unidades por quantidade
            const unids = Object.entries(d.porUnid).filter(([u,q])=>q>0).sort((a,b)=>b[1]-a[1]);
            return(<tr key={d.disc} style={S.trLine}>
              <td style={{...TDl,borderLeft:'4px solid '+cor,fontWeight:700,color:V.br}}>{d.disc}</td>
              <td style={{...TD,fontWeight:700,color:V.br}}>{fmtBRL(d.tot,0)}</td>
              <td style={TD}>{pct.toFixed(1)}%</td>
              <td style={{...TD,fontWeight:700,color:cor}}>{fmtBRL(rkm,0)}/km</td>
              <td style={TDl}>
                {unids.map(([u,q],i)=>(
                  <span key={u} style={{display:'inline-block',marginRight:14,fontSize:10}}>
                    <b style={{color:V.br}}>{fmtNum(q,0)} {u}</b>
                    <span style={{color:V.gr,marginLeft:4}}>· {fmtNum(extLote?q/extLote:0,1)} {u}/km</span>
                  </span>
                ))}
              </td>
            </tr>);
          })}
          <tr style={S.tfootRow}>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700}}>TOTAL</td>
            <td style={{padding:'8px 10px',fontSize:12,fontWeight:700,textAlign:'right',color:V.br}}>{fmtBRL(totSicro)}</td>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right'}}>100,0%</td>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right'}}>{fmtBRL(extLote?totSicro/extLote:0,0)}/km</td>
            <td style={{padding:'8px 10px',fontSize:10,color:V.gr,fontStyle:'italic'}}>—</td>
          </tr>
        </tbody>
      </table>
    </div>

    {/* BLOCO 3 · DETALHE POR RODOVIA — comparação por tipo de obra */}
    <div style={{fontSize:11,fontWeight:700,color:V.br,letterSpacing:.4,marginBottom:8,paddingBottom:4,borderBottom:'2px solid '+V.bl}}>
      ▣ BLOCO 3 · DETALHE POR RODOVIA · comparação dos tipos de obra
    </div>
    <div style={{...S.card,overflow:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:140,position:'sticky',left:0,background:V.g0,zIndex:2}}>Rodovia</th>
            <th style={TH}>Ext (km)</th>
            <th style={TH}>R$ rateado</th>
            {tipos.map(t=>(<th key={t.k} style={{...TH,minWidth:75,background:tipoCores[t.k]+'22',color:tipoCores[t.k],fontSize:9}}>
              {t.l}<br/><span style={{fontSize:8.5,color:V.gr,fontWeight:500}}>({t.unid})</span>
            </th>))}
          </tr>
        </thead>
        <tbody>
          {sistema.map(r=>{
            const fr = extLote?r.ext/extLote:0;
            const rR = totSicro*fr;
            return(<tr key={r.rod} style={S.trLine}>
              <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1}}>{r.rod}</td>
              <td style={TD}>{fmtNum(r.ext,1)}</td>
              <td style={{...TD,fontWeight:700,color:V.bl}}>{fmtBRL(rR,0)}</td>
              {tipos.map(t=>{
                const v = sumTipo(t,r.rod);
                return(<td key={t.k} style={{...TD,padding:'6px 4px',textAlign:'center',fontFamily:'monospace',background:tipoCores[t.k]+'08',color:v?V.br:V.gr,fontWeight:v?700:400}}>
                  {v?(t.field==='km'?fmtNum(v,1):v):'—'}
                </td>);
              })}
            </tr>);
          })}
          <tr style={S.tfootRow}>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700,position:'sticky',left:0,background:V.g0,borderRight:'1px solid '+V.b1}}>TOTAL</td>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right'}}>{fmtNum(extLote,1)}</td>
            <td style={{padding:'8px 10px',fontSize:11,fontWeight:700,textAlign:'right',color:V.bl}}>{fmtBRL(totSicro,0)}</td>
            {tipos.map(t=>{
              const tot = sumTipo(t);
              return(<td key={t.k} style={{padding:'8px 4px',fontSize:10,fontWeight:700,textAlign:'center',background:tipoCores[t.k]+'33'}}>
                {tot?(t.field==='km'?fmtNum(tot,1):tot):'—'}
              </td>);
            })}
          </tr>
        </tbody>
      </table>
    </div>
    <div style={{...S.cardP,marginTop:10,fontSize:10,color:V.gr,lineHeight:1.5}}>
      <b>Como ler:</b> a Bloco 3 mostra <b>quantidade física por tipo de obra</b> em cada rodovia. Útil para identificar concentrações: qual rodovia tem mais duplicação? Mais OAEs? Mais marginais?
      <br/>O R$ rateado por rodovia é proporcional à extensão (extensão_rod ÷ extensão_lote × R$ total SICRO).
    </div>

  </div>);
}

export { TipoRodoviaPageV2 };
export default TipoRodoviaPageV2;
